import React, { useEffect, useState, useCallback } from "react";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import googleDriveAxios from "../../common/googleDriveAxios";

const QAGreenTagChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState<boolean>(false);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [plumbing, setPlumbing] = useState<Checklist[]>([]);
  const [electrical, setElectrical] = useState<Checklist[]>([]);
  const [externalFixtureAndFittings, setExternalFixtureAndFittings] = useState<
    Checklist[]
  >([]);
  const [internalFixtureAndFittings, setInternalFixtureAndFittings] = useState<
    Checklist[]
  >([]);
  const [fitmentCheck, setFitmentCheck] = useState<Checklist[]>([]);
  const [warrantyBagsAndExtra, setWarrantyBagsAndExtra] = useState<Checklist[]>(
    []
  );
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "qa_green_tag",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isRadioChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isRadioChanged]);

  const prepareData = useCallback(async () => {
    setLoading(true);
    try {
      if (!chassisNo) {
        setPlumbing([]);
        setElectrical([]);
        setExternalFixtureAndFittings([]);
        setInternalFixtureAndFittings([]);
        setFitmentCheck([]);
        setWarrantyBagsAndExtra([]);
        setModel("");
        setLocalCheckedBy("");
        setCheckedBy("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        setLoading(false);
        return;
      }

      setPlumbing([]);
      setElectrical([]);
      setExternalFixtureAndFittings([]);
      setInternalFixtureAndFittings([]);
      setFitmentCheck([]);
      setWarrantyBagsAndExtra([]);
      setModel("");
      setLocalCheckedBy("");
      setCheckedBy("");

      const response = await axiosPrivate.post(
        `prepare_qa_green_tag/${chassisNo}`
      );
      const data = response.data;

      const plumbingData = (data.plumbing || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const electricalData = (data.electrical || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const extFixData = (data.externalFixtureAndFittings || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const intFixData = (data.internalFixtureAndFittings || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );
      const fitmentData = (data.fitmentCheck || []).map((item: Checklist) => ({
        ...item,
        status: item.status ? item.status.trim().toLowerCase() : "not checked",
      }));
      const warrantyData = (data.warrantyBagsAndExtra || []).map(
        (item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        })
      );

      setPlumbing(plumbingData);
      setElectrical(electricalData);
      setExternalFixtureAndFittings(extFixData);
      setInternalFixtureAndFittings(intFixData);
      setFitmentCheck(fitmentData);
      setWarrantyBagsAndExtra(warrantyData);
      setModel(data.model || "");
      setLocalCheckedBy(data.checkedBy || "");
      setCheckedBy(data.checkedBy || "");

      const allChecklists = [
        ...plumbingData,
        ...electricalData,
        ...extFixData,
        ...intFixData,
        ...fitmentData,
        ...warrantyData,
      ];
      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");

      const formattedDate = data.createdOn
        ? new Date(data.createdOn).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "";
      setDate(formattedDate || "");
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  }, [
    chassisNo,
    setModel,
    setCheckedBy,
    axiosPrivate,
    setPlumbing,
    setElectrical,
    setExternalFixtureAndFittings,
    setInternalFixtureAndFittings,
    setFitmentCheck,
    setWarrantyBagsAndExtra,
    setLocalCheckedBy,
    setCompleteStatus,
    setIsRadioChanged,
    setLoading,
    setDate,
  ]);

  useEffect(() => {
    prepareData();
  }, [chassisNo, model, prepareData]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }
    setLoading(true);
    try {
      const allChecklists = [
        ...plumbing,
        ...electrical,
        ...externalFixtureAndFittings,
        ...internalFixtureAndFittings,
        ...fitmentCheck,
        ...warrantyBagsAndExtra,
      ];

      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status &&
            ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
        );

      const payload = {
        chassisNo: chassisNo,
        model: model,
        plumbing: plumbing.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        electrical: electrical.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        externalFixtureAndFittings: externalFixtureAndFittings.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        internalFixtureAndFittings: internalFixtureAndFittings.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        fitmentCheck: fitmentCheck.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        warrantyBagsAndExtra: warrantyBagsAndExtra.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy: checkedBy,
        createdOn: date,
        completeStatus: isComplete ? "Complete" : "Incomplete",
      };

      await toast.promise(
        axiosPrivate.post("create_or_update_qa_green_tag", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...plumbing,
          ...electrical,
          ...externalFixtureAndFittings,
          ...internalFixtureAndFittings,
          ...fitmentCheck,
          ...warrantyBagsAndExtra,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "qa_green_tag_rework",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "qa_green_tag_rework" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (index: number, value: any, type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "plumbing") {
      updateChecklist(plumbing, setPlumbing);
    }
    if (type === "electrical") {
      updateChecklist(electrical, setElectrical);
    }
    if (type === "extFix") {
      updateChecklist(
        externalFixtureAndFittings,
        setExternalFixtureAndFittings
      );
    }
    if (type === "intFix") {
      updateChecklist(
        internalFixtureAndFittings,
        setInternalFixtureAndFittings
      );
    }
    if (type === "fitment") {
      updateChecklist(fitmentCheck, setFitmentCheck);
    }
    if (type === "warranty") {
      updateChecklist(warrantyBagsAndExtra, setWarrantyBagsAndExtra);
    }
  };

  const handleNotesChange = (index: number, value: any, type: string) => {
    if (completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "plumbing") {
      updateChecklist(plumbing, setPlumbing);
    }
    if (type === "electrical") {
      updateChecklist(electrical, setElectrical);
    }
    if (type === "extFix") {
      updateChecklist(
        externalFixtureAndFittings,
        setExternalFixtureAndFittings
      );
    }
    if (type === "intFix") {
      updateChecklist(
        internalFixtureAndFittings,
        setInternalFixtureAndFittings
      );
    }
    if (type === "fitment") {
      updateChecklist(fitmentCheck, setFitmentCheck);
    }
    if (type === "warranty") {
      updateChecklist(warrantyBagsAndExtra, setWarrantyBagsAndExtra);
    }
  };

  const allChecklists = [
    ...plumbing,
    ...electrical,
    ...externalFixtureAndFittings,
    ...internalFixtureAndFittings,
    ...fitmentCheck,
    ...warrantyBagsAndExtra,
  ];

  const checklistsWithSerialNumbers = [
    {
      data: plumbing,
      type: "plumbing",
      subHeaderText: "Plumbing",
    },
    {
      data: electrical,
      type: "electrical",
      subHeaderText: "Electrical",
    },
    {
      data: externalFixtureAndFittings,
      type: "extFix",
      subHeaderText: "External Fixture & Fittings",
    },
    {
      data: internalFixtureAndFittings,
      type: "intFix",
      subHeaderText: "Internal Fixture & Fittings",
    },
    {
      data: fitmentCheck,
      type: "fitment",
      subHeaderText: "Fitment Check ('Shake-Pull' Test)",
    },
    {
      data: warrantyBagsAndExtra,
      type: "warranty",
      subHeaderText: "Warranty Bag & Extra",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>

      <Grid container mt={2}>
        <Grid item md={12}>
          <TableContainer
            sx={{
              position: "relative",
              filter: loading ? "blur(5px)" : "none",
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            <Table aria-label="chassis prep checklist">
              <TableHead>
                <TableRow className="table__head_row">
                  <TableCell
                    className="table__head_cell"
                    sx={{
                      width: "1%",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "0px",
                    }}
                  ></TableCell>
                  <TableCell
                    className="table__head_cell"
                    sx={{
                      width: "1%",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "0px",
                    }}
                  >
                    S/N
                  </TableCell>
                  <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                    CHECKLIST
                  </TableCell>
                  <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                    NOTES
                  </TableCell>
                  <TableCell className="table__head_cell" sx={{ width: "5%" }}>
                    STATUS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={
                        allChecklists.length > 0 &&
                        allChecklists.every(
                          (item) => item.status?.toLowerCase() === "not checked"
                        )
                      }
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"qa_green_tag"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked by:"
            variant="outlined"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date:"
            variant="outlined"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </>
  );
};

export default QAGreenTagChecklist;
